import ApiBase from 'apis/ApiBase';
import config from 'config';
import { List, PaginatedResult } from 'types/api';
import {
  DetailedUserProfile,
  IntercomFeedback,
  PatchUserPayload,
  DetailedShortlist,
  Shortlist,
  UserProfile,
  PatchShortlist,
} from 'types/user';
const baseUrl = config.COMPANIES_API_URL;

const api = ApiBase();

export type ShortlistedCompaniesOrder = 'latest-activity' | 'recently-added' | 'name';

export interface ShortlistedCompaniesParams {
  shortlistId?: UUID;
  orderBy: ShortlistedCompaniesOrder;
  raising?: boolean;
}

export interface UserFeedParams {
  startTime?: string;
  endTime?: string;
  audience?: 'following' | 'shortlisted' | 'all';
  sortBy?: 'date' | 'relevance';
  limit?: number;
  page?: number;
}

export const userAPIUrls = {
  self: {
    detailed: `${baseUrl}/userProfiles/me/detailed`,
  },
  userProfiles: {
    list: () => `${baseUrl}/userProfiles/byCwUserId`,
    getDetailed: (cwUserId: string) => `${baseUrl}/userProfiles/byCwUserId/${cwUserId}/detailed`,
  },
  shortlists: {
    list: () => `${baseUrl}/company-shortlists`,
    companies: (params: ShortlistedCompaniesParams) => {
      const searchParams = new URLSearchParams();
      if (params.shortlistId) searchParams.append('shortlistId', params.shortlistId);
      searchParams.append('orderBy', params.orderBy);
      if (params.raising) searchParams.append('raising', 'true');
      return `${baseUrl}/company-shortlists/companies?${searchParams.toString()}`;
    },
    suggestions: (shortlistId: UUID) => `${baseUrl}/company-shortlists/${shortlistId}/suggestions`,
  },
  followerRequests: {
    listSent: () => `${baseUrl}/users/follower-requests/sent`,
  },
  matches: {
    featured: () => `${baseUrl}/matches/featured`,
  },
  uiState: {
    get: () => `${baseUrl}/users/me/ui-state`,
    update: () => `${baseUrl}/users/me/ui-state`,
  },
  feed: (params?: UserFeedParams) => {
    const url = `${baseUrl}/users/me/feed`;
    if (!params) return url;

    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });

    const queryString = searchParams.toString();
    return queryString ? `${url}?${queryString}` : url;
  },
  feedImpressions: () => `${baseUrl}/users/me/feed/impressions`,
  postableFeeds: (params?: { limit?: number; page?: number }) => {
    const url = `${baseUrl}/users/me/postable-feeds`;
    if (!params) return url;

    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });

    const queryString = searchParams.toString();
    return queryString ? `${url}?${queryString}` : url;
  },
  postableSources: () => `${baseUrl}/users/me/postable-sources`,
};

export const usersApi = {
  myNetwork: (nameFilter: string) => api.get<List<UserProfile>>(`${baseUrl}/users/me/network?name=${nameFilter}`),
  /** Records that the user has seen these posts in their feed. Max 50 ids per call; returns 204. */
  logFeedImpressions: (contentIds: UUID[]) => api.post<void>(userAPIUrls.feedImpressions(), { contentIds }),
  isConnected: (userId: UUID) => api.get<boolean>(`${baseUrl}/users/${userId}/is-connected`),
  feedback: (body: IntercomFeedback) => api.post(`${baseUrl}/users/feedback`, body),
  emailFeedback: (token: UUID, body: object) => api.post(`${config.NOTIFY_API_URL}/feedback/${token}`, body),
  shortlists: {
    get: (listId: UUID) => api.get<DetailedShortlist>(`${baseUrl}/company-shortlists/${listId}`),
    create: (listName?: string, description?: string) =>
      api.post<Shortlist>(`${baseUrl}/company-shortlists`, { name: listName, description }),
    duplicate: (listId: UUID, payload: PatchShortlist) =>
      api.post<Shortlist>(`${baseUrl}/company-shortlists/${listId}/duplicate`, payload),
    update: (listId: UUID, payload: PatchShortlist) =>
      api.patch<Shortlist>(`${baseUrl}/company-shortlists/${listId}`, payload),
    delete: (listId: UUID) => api.deleteRequest<UUID>(`${baseUrl}/company-shortlists/${listId}`),
    // Adds the company to the user's oldest list ("My watchlist" by default), creating it if needed
    addToDefault: (companyId: number | string) =>
      api.post<Shortlist>(`${baseUrl}/company-shortlists/default/companies/${companyId}`, {}),
    updateCompanies: (listId: UUID, companyIds: string[], communityContextSlug?: string) =>
      api.put<Shortlist>(`${baseUrl}/company-shortlists/${listId}/companies`, {
        companyIds,
        communityContextSlug,
      }),
  },
  userProfiles: {
    get: (cwUserId: string) => api.get<UserProfile>(`${baseUrl}/userProfiles/byCwUserId/${cwUserId}`),
    getDetailed: (cwUserId: string) => {
      return api.get<DetailedUserProfile>(`${baseUrl}/userProfiles/byCwUserId/${cwUserId}/detailed`);
    },
    list: (cwUserIds: string[]) => {
      return api.post<PaginatedResult<UserProfile>>(userAPIUrls.userProfiles.list(), {
        cwUserIds,
      });
    },
    patchProfile: (body: PatchUserPayload) => {
      return api.patch<DetailedUserProfile>(`${baseUrl}/users`, body);
    },
    updateProfileImage: (url?: string) =>
      api.put(`${baseUrl}/users/image`, {
        imageURL: url,
      }),
  },
};
